<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" scrollable
                   :title="computedTitle" @show="onShow" body-class="pb-3"
                   @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')" :is-loading="loading">


      <div class="row" v-if="reservation && reservation.lot_no">
        <div class="col-lg-6">
          <div class="row">
            <div v-bind:class="[lotFinished ? 'col-md-9' : 'col-md-12']">
              <bidder-select-input-widget :model="model" attribute="bidder_uuid" :items="items"
                                          value-field="uuid"
                                          :auction-uuid="reservation.auction_uuid"/>
            </div>
            <div v-if="lotFinished" class="col-md-3" style="margin-top: 2em">
              <b-button variant="primary" @click="onBiddersModalClick">
                <font-awesome-icon :icon="'user-plus'"/>
                {{ $t('New Bidder') }}
              </b-button>
            </div>
            <div class="col-md-12">
              <currency-input-widget :model="model" attribute="amount"/>
            </div>
            <div class="col-md-12">
              <input-widget :model="model" attribute="comment" type="textarea"/>
            </div>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                {{ $t("Bids") }}
              </h5>
            </div>
            <div class="card-body pt-0">
              <BidsTable hide-per-page-selector hide-export hide-search
                          :exclude-columns="['actions', 'stock_no','title', 'lot_no']"/>
            </div>
          </div>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import InputWidget from "../../../core/components/input-widget/InputWidget.vue";
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal.vue";
  import NegotiationModel from "@/modules/lots/models/Negotiation.model";
  import BidsTable from "@/modules/bids/BidsTable.vue";
  import BidderSelectInputWidget from "@/core/widgets/BidderSelectInputWidget.vue";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget.vue";
  import LotsService from "@/modules/lots/LotsService";

  const {mapState, mapActions} = createNamespacedHelpers('lots');
  const {mapActions: bidsMapActions} = createNamespacedHelpers('bids');
  const {mapActions: biddersMapActions} = createNamespacedHelpers('bidders');
  const {mapState: mapStateGlobal, mapActions: mapActionsGlobal} = createNamespacedHelpers('global');
  import {SOURCE_NEGOTIATIONS_MODAL} from "../../constants";
  import helperService from "@/core/services/helper.service";

  export default {
    name: "NegotiationModal",
    components: {
      CurrencyInputWidget,
      BidderSelectInputWidget,
      BidsTable,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        loading: true,
        showConfirmation: true,
        model: new NegotiationModel(),
        items: [],
        reservationsProposals: []
      }
    },
    computed: {
      ...mapState({
        modal: state => state.negotiationModal,
        reservation: state => state.negotiationModal.reservation,
        bidder: state => state.negotiationModal.bidder,
        sale: state => state.negotiationModal.sale,
      }),
      ...mapStateGlobal({
        liveAuctionProvider: state => state.liveAuctionProvider
      }),
      computedTitle() {
        if (this.reservation) {
          return this.$i18n.t("Negotiations for Lot {lot_no}", {lot_no: this.reservation.lot_no})
        }
        return this.$i18n.t("Negotiations")
      },
      lotFinished() {
        if (this.liveAuctionProvider) {
          return true
        }
        const auction = this.modal.reservation.auction;
        return auction.status === 'completed';
      }
    },
    watch: {
      bidder: {
        deep: true,
        handler(newVal) {
          if (!newVal)
            return;

          this.items = [newVal];
          this.model.bidder_uuid = newVal.uuid;
        }
      }
    },
    methods: {
      ...mapActions(['hideNegotiationModal', 'resetTable']),
      ...bidsMapActions(['updateTableSource']),
      ...biddersMapActions({
        showBiddersModal: 'showModal'
      }),
      async onShow() {
        this.showConfirmation = true;
        await this.updateTableSource({
          source: 'negotiationModal',
          extraFilters: {lot_no: this.reservation.lot_no, auction_uuid: this.reservation.auction_uuid},
          sale: this.sale,
          ordering: '-amount',
          clearBeforeUse: true
        });
        this.reservationsProposals = (await LotsService.getLatestReservationProposals({reservation_uuids: [this.reservation.uuid]})).body
        this.loading = false
      },
      onHidden() {
        this.model = new NegotiationModel();
        this.reservationsProposals = []
        this.hideNegotiationModal();
      },
      async onSubmit() {
        if (this.reservationsProposals.length) {
          const confirm = await helperService.showProposalsConfirmationModal(this.reservationsProposals, this.$confirm, this.$router, this.$options.filters)
          if (!confirm) return
        }

        if (this.reservation.status === 'sold' && this.reservation.lot.bidder) {
          const confirm = await this.$confirm(
            this.$i18n.t('Lot {lot_no} has already been sold to Buyer {buyer}. Proceeding will override the existing record. Are you sure you want to proceed?',
            {lot_no: this.reservation.lot_no, buyer: this.reservation.lot.bidder.customer.display_name})
          )
          if (!confirm) return
        }

        this.showConfirmation = false;
        this.loading = true;
        let requestData = {...this.model.toJSON(), lot_uuid: this.reservation.lot.uuid, type: 'negotiation_bid'}
        let response = await LotsService.createNegotiationBid(requestData);
        this.loading = false;

        if (response.success) {
          this.$toast('success', this.$i18n.t("Negotiation bid was successfully created."));
          this.hideNegotiationModal();
          this.resetTable();
        } else {
          this.showConfirmation = true;
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            this.model.setMultipleErrors(response.body);
          }
        }
      },
      onBiddersModalClick() {
        this.showBiddersModal({
          object: {
            auction: this.reservation.auction,
            auction_uuid: this.reservation.uuid
          },
          source: SOURCE_NEGOTIATIONS_MODAL
        });
      },
    }
  }
</script>

<style scoped>

</style>
